import { createContext, useContext, type ReactNode, type RefObject } from 'react';
import type { ToastItem, ToastRef } from '@allygpo/ally-ux';

/**
 * The message shape the app's 50-odd toast calls use (PrimeReact's, kept so they didn't all have to change).
 * `summary` becomes the bold title and `detail` the message; with only one of them, it is the message.
 */
export interface AppToastMessage {
  severity?: 'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast';
  summary?: ReactNode;
  detail?: ReactNode;
  /** Milliseconds before it hides. */
  life?: number;
  /** Stays until dismissed. */
  sticky?: boolean;
}

/** What `useToast()` returns: show() takes the app's message shape and renders Ally UX's Toast. */
export interface AppToast {
  show: (message: AppToastMessage | AppToastMessage[]) => void;
  /** Ally UX's Toast can't remove a single message, so this hides every toast on screen. */
  clear: () => void;
  /** Show an Ally UX ToastItem directly (used by notify). */
  showItem: (item: ToastItem) => void;
}

const toItem = ({ severity, summary, detail, life, sticky }: AppToastMessage): ToastItem => {
  const variant: ToastItem['variant'] = severity === 'secondary' || severity === 'contrast' ? 'neutral' : severity ?? 'info';
  const hasBoth = summary != null && detail != null;
  return {
    variant,
    title: hasBoth && typeof summary === 'string' ? summary : undefined,
    message: hasBoth ? detail : (detail ?? summary ?? ''),
    autoHide: !sticky,
    ...(life ? { duration: life } : {}),
  };
};

/** Wraps the Ally UX Toast instance so callers keep the app's message shape. */
export const createAppToast = (ref: RefObject<ToastRef | null>): AppToast => ({
  show: (message) => ref.current?.show(Array.isArray(message) ? message.map(toItem) : toItem(message)),
  clear: () => ref.current?.clearAll(),
  showItem: (item) => ref.current?.show(item),
});

export const ToastContext = createContext<RefObject<AppToast | null> | null>(null);

/** Call toast helpers from any component inside Layout. */
export const useToast = (): RefObject<AppToast | null> => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider (Layout)');
  return ctx;
};
